// 郵便番号→住所の自動入力（zipcloud API・失敗時は無反応で手入力継続）
const zipAutofill = () => {
    const zip = document.querySelector("input[name='zip']");
    if (!zip) return;

    const prefSelect = document.querySelector("select[name='pref']");
    const cityInput = document.querySelector("input[name='city']");
    const addressInput = document.querySelector("input[name='address']");
    const buildingInput = document.querySelector("input[name='building']");

    // ハイフンや全角混じりの入力を7桁の数字だけに正規化する
    const normalize = (value) => value.replace(/[^0-9]/g, "");

    // いま住所欄に反映されている郵便番号（7桁の正規化済み文字列）。比較用。
    // 上書き確認をキャンセルした郵便番号はここに入らない。キャンセル時は郵便番号欄を
    // 元の値に巻き戻すので、断られた番号はそもそも画面上に残らないため。
    //
    // blur のたびに fill() が走るため、「郵便番号が変わったときだけ上書きする」という
    // 条件がここの肝になる。無条件に上書きすると、自動入力後に市区町村や番地を
    // 手直しした利用者が郵便番号欄を通過しただけで、その修正が黙って消える。
    // 逆に「未入力のときだけ埋める」に戻すと、郵便番号を入れ直しても住所が
    // 更新されないという元の不具合に戻る。どちらへも寄せず、必ず
    // 「いま反映されている郵便番号と違うか」で判定すること。
    let settledZip = "";

    // 上記を確定した時点の郵便番号欄の「生の」入力値。キャンセル時の巻き戻し先。
    // 正規化後の7桁ではなく生の値を持つのは、利用者が「370-0000」と入力していたのを
    // 「3700000」に戻すと、断っただけなのに表記を勝手に書き換えることになるため。
    let settledZipInput = "";

    // 郵便番号ごとの検索結果（応答待ちの Promise を含む）を記憶する。
    // 7桁目を打った時点の input と、欄を離れた時点の blur の両方が fill() を呼ぶので、
    // 記憶しないと一度の入力で zipcloud を2回叩くことになる。Promise ごと持たせて
    // おけば、通信の往復中に blur が来ても2本目のリクエストは発生しない。
    // 失敗（通信エラー・該当なし）は残さず消し、次の入力で再度問い合わせられるようにする。
    const cache = {};

    const lookup = (code) => {
        if (!cache[code]) {
            cache[code] = fetch(
                `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${code}`
            )
                .then((res) => res.json())
                .then((data) => {
                    const r = data?.results?.[0];
                    if (!r) throw new Error("no result");
                    return r;
                });
            // 失敗はキャッシュに残さない
            cache[code].catch(() => {
                delete cache[code];
            });
        }
        return cache[code];
    };

    // 上書きで失われる内容があるか。
    // 建物名（building）は自動入力で埋める欄ではないので判定に含めない。
    // ここに含めると、建物名だけ先に入力した利用者が郵便番号を入れたときにまで
    // 確認ダイアログが出てしまう。その建物名は「これから完成させる住所」のもので、
    // 古くもなければ失われもしない。
    const hasAddressValue = () =>
        (prefSelect && prefSelect.value !== "") ||
        (cityInput && cityInput.value !== "") ||
        (addressInput && addressInput.value !== "");

    // 取得した住所を各欄へ反映する。
    // clearBuilding は「上書き確認で承諾を得た」経路だけが true にする。
    // apply() の中で状態から呼び出し元を推測せず、呼び出し側で明示すること。
    const apply = (code, result, { clearBuilding }) => {
        if (prefSelect) {
            const opt = [...prefSelect.options].find(
                (o) => o.textContent.trim() === result.address1
            );
            if (opt) {
                // 都道府県は hidden な native select ＋ 独自UI（_select.js）の組み合わせ。
                // 画面に見えているラベルは native select の change でしか同期されないため、
                // value を代入するだけでは表示がプレースホルダーのまま変わらない。
                // change の発火は必須（_select.js のオプションクリックと同じ手順）。
                prefSelect.value = opt.value;
                prefSelect.dispatchEvent(
                    new Event("change", { bubbles: true })
                );
            }
        }
        if (cityInput) {
            cityInput.value = result.address2;
            // 入力監視（バリデーションや文字数カウント等）に変更を伝えるため input を発火
            cityInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
        if (addressInput) {
            addressInput.value = result.address3;
            addressInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
        if (clearBuilding && buildingInput) {
            // 建物名は「古い住所」に紐づく情報。別の場所へ差し替えることに承諾を得た以上、
            // マンション名や部屋番号をそのまま残すと、違う住所に付いたまま送信されてしまう。
            // 消してよいのは承諾を得たこの経路だけ。確認なしで埋める経路では触らない。
            buildingInput.value = "";
            buildingInput.dispatchEvent(new Event("input", { bubbles: true }));
        }
        settledZip = code;
        settledZipInput = zip.value;
    };

    // 読み込み時点で郵便番号と住所が揃っているなら、その郵便番号が反映済みとみなす。
    // 入力チェックで確認画面から戻された直後は old() により各欄が復元されているので、
    // 郵便番号欄にフォーカスして離れただけで利用者の修正を上書きしたり、
    // 何もしていないのに確認ダイアログを出したりしてはいけない。
    const seedSettledZip = () => {
        const code = normalize(zip.value);
        if (code.length !== 7) return;
        if (hasAddressValue()) {
            settledZip = code;
            settledZipInput = zip.value;
        }
    };

    // canPrompt: 上書き確認のダイアログを出してよい経路か。
    // 入力中（input）の途中で確認を突き付けると、7桁目を打った瞬間に打鍵が止められて
    // 唐突に感じられる。確認は欄を離れた（blur）ときだけに限り、入力中は
    // 「確認が要らない場合＝失う中身が無い場合」だけ黙って埋める。
    const fill = async ({ canPrompt }) => {
        const code = normalize(zip.value);
        if (code.length !== 7) return;
        // 郵便番号が反映済みのものから変わっていなければ、手入力の修正を優先して何もしない
        if (code === settledZip) return;
        try {
            // 入力中でも問い合わせ自体は先に走らせる。応答は郵便番号ごとにキャッシュ
            // されるので、blur 時の判断が待ち時間なしで済む（今までの体感速度を保つ）。
            const result = await lookup(code);

            // 通信の往復中や確認ダイアログの表示中に、もう一方の経路（input と blur）が
            // 同じ郵便番号を反映させていることがある。二重適用・二重の確認を防ぐ。
            if (code === settledZip) return;

            // 承諾を得た上書きなのか、確認不要で黙って埋めるだけなのかを持ち回る。
            // 建物名を消すのは前者だけ。
            let clearBuilding = false;

            // 確認を出すのは「郵便番号が変わった」かつ「失う中身がある」ときだけ。
            // 空欄しかないなら奪うものが無いので黙って埋める。
            // また、確認は検索が成功した後にだけ出す。宛先不明や通信断のときに
            // 上書きの可否を尋ねても、そもそも入れる住所が無く利用者を煩わせるだけ。
            if (hasAddressValue()) {
                // 入力中は確認も反映もせず、判断は blur に委ねる。
                // ここで反映済みとして記録してしまうと、続く blur が確認を出さなくなる。
                if (!canPrompt) return;

                const address =
                    result.address1 + result.address2 + result.address3;
                // 建物名が入っているときだけ、それも消える旨を一文足す。
                // 空のときに書いても該当しない注意書きで迷わせるだけなので、通常は短いまま。
                const buildingNotice =
                    buildingInput && buildingInput.value !== ""
                        ? "建物名も消去されます。"
                        : "";
                if (
                    !window.confirm(
                        `入力済みの住所を「${address}」で上書きしますか？${buildingNotice}`
                    )
                ) {
                    // キャンセルされたら住所欄は一切触らず（部分適用はしない）、
                    // 郵便番号欄も確定時の値に巻き戻す。断られた郵便番号を残すと
                    // 「郵便番号は新しい・住所は古い」という食い違った状態になり、
                    // 画面からはその不一致に気付けないため。
                    zip.value = settledZipInput;
                    zip.dispatchEvent(new Event("input", { bubbles: true }));
                    // フォーカスは動かさない。blur の中で focus() を呼び戻すと
                    // フォーカスとblurのループになり、利用者のタブ移動も妨げる。
                    return;
                }

                clearBuilding = true;
            }

            apply(code, result, { clearBuilding });
        } catch (e) {
            // オフライン/CSP・該当なしのときは手入力にフォールバック
        }
    };

    seedSettledZip();

    zip.addEventListener("blur", () => fill({ canPrompt: true }));
    zip.addEventListener("input", () => {
        if (normalize(zip.value).length === 7) fill({ canPrompt: false });
    });
};
export default zipAutofill;
